/**
 * /customer/settings — Account & app-level preferences (W-Apple).
 *
 * Minimal in this dispatch: only the Account sub-route is wired (it
 * houses the App-Store-mandated Delete Account flow). Other rows are
 * stubbed for navigation consistency.
 */
import { Link } from 'react-router-dom';
import { UserCog, Bell, Globe, ChevronRight, ArrowLeft } from 'lucide-react';

export default function SettingsPage() {
  return (
    <div className="space-y-6" data-testid="customer-settings-screen">
      <header className="flex items-center gap-3">
        <Link
          to="/customer/profile"
          className="p-2 -ml-2 rounded-full hover:bg-white/5 text-white/70"
          data-testid="settings-back"
          aria-label="Back to profile"
        >
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
          Settings
        </h1>
      </header>

      <nav className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/5">
        <SettingsRow
          to="/customer/settings/account"
          icon={<UserCog size={18} />}
          label="Account"
          description="Email, password, delete account"
          testid="settings-row-account"
        />
        <SettingsRow
          to="/customer/notifications"
          icon={<Bell size={18} />}
          label="Notifications"
          description="In-app inbox"
          testid="settings-row-notifications"
        />
        <SettingsRow
          to="/customer/help"
          icon={<Globe size={18} />}
          label="Help & Language"
          description="Get support, language preference"
          testid="settings-row-help"
        />
      </nav>
    </div>
  );
}

function SettingsRow({ to, icon, label, description, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-4 p-4 hover:bg-white/[0.03] transition"
    >
      <span className="w-9 h-9 rounded-full bg-[#1F1F26] flex items-center justify-center text-[#D4AF37]">
        {icon}
      </span>
      <span className="flex-1">
        <span className="block text-white font-medium">{label}</span>
        <span className="block text-white/50 text-sm">{description}</span>
      </span>
      <ChevronRight size={18} className="text-white/30" />
    </Link>
  );
}
